// Shared ECharts option fragments for the v2 dashboard so every time-series chart offers the
// same interaction set: inside + slider zoom on the X axis, a toolbox dataZoom/brush, and a
// horizontal (lineX) brush for selecting a range of buckets to zoom into.

// Zoom via the bottom slider (drag the handles to zoom to a range) + inside wheel/drag. No
// toolbox/brush buttons — they crowd the top corners and collide with the legend/axis names.
export const timeSeriesZoom = {
  dataZoom: [
    { type: 'inside', xAxisIndex: 0 },
    { type: 'slider', xAxisIndex: 0, height: 18, bottom: 28 },
  ],
} as const;

// Grid that leaves room for the slider + legend at the bottom.
export const timeSeriesGrid = { left: 8, right: 8, top: 16, bottom: 64, containLabel: true } as const;

// Compact grid for distribution/histogram charts (no slider).
export const distributionGrid = { left: 8, right: 16, top: 16, bottom: 24, containLabel: true } as const;

// A chart counts as "empty" when it has no buckets, or every plotted value is zero / null /
// undefined — in that case sections render the DashboardEmptyState instead of empty axes.
export const isChartDataEmpty = (values: (number | null | undefined)[]): boolean => values.length === 0 || values.every(v => !v);

export interface AlignedAxisBounds {
  min: number;
  max: number;
  interval: number;
}

// Round an interval up to a "nice" human-readable step (1 / 2 / 2.5 / 5 × 10^n).
const niceInterval = (raw: number): number => {
  if (!(raw > 0) || !Number.isFinite(raw)) return 1;
  const base = Math.pow(10, Math.floor(Math.log10(raw)));
  const frac = raw / base;
  const nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
  return nice * base;
};

// Compute min/max/interval for two y axes so that zero sits at the same pixel height on both.
// ECharts picks each axis' range independently, so a chart with one negative-going series (or
// simply different data shapes) draws two different zero lines — visually misleading when the
// two series are meant to be read against a common baseline.
//
// Both axes end up with the same number of tick steps above and below zero; only the step size
// differs, so each axis keeps a range that fits its own data. Pass the *plotted* values (for
// stacked series, the stack totals).
export const alignZeroAxes = (leftValues: (number | null | undefined)[], rightValues: (number | null | undefined)[], splitNumber = 5): [AlignedAxisBounds, AlignedAxisBounds] => {
  const bounds = [leftValues, rightValues].map(values => {
    const nums = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    // Zero is always included so the shared baseline is inside both ranges.
    const max = Math.max(0, ...nums);
    const min = Math.min(0, ...nums);
    const interval = niceInterval((max - min) / splitNumber);
    return { interval, posSteps: Math.ceil(max / interval), negSteps: Math.ceil(-min / interval) };
  });

  // Unify the step counts on each side of zero; guard the all-zero case so the axis keeps a range.
  const posSteps = Math.max(1, ...bounds.map(b => b.posSteps));
  const negSteps = Math.max(...bounds.map(b => b.negSteps));

  return bounds.map(({ interval }) => ({ min: -negSteps * interval, max: posSteps * interval, interval })) as [AlignedAxisBounds, AlignedAxisBounds];
};